<script setup>
import { useRepo } from 'pinia-orm';
import { computed, ref } from 'vue';
import PostMediaSection from './PostMediaSection.vue';
import AvailabilityBadge from '@/components/posts/AvailabilityBadge.vue';
import LastCollectedLine from '@/components/posts/LastCollectedLine.vue';
import PostActions from '@/components/posts/PostActions.vue';
import PostContent from '@/components/posts/PostContent.vue';
import PostHeader from '@/components/posts/PostHeader.vue';
import ProductCard from '@/components/posts/ProductCard.vue';
import ProductSuggestionDoor from '@/components/posts/ProductSuggestionDoor.vue';
import { postModelForFeed } from '@/models/posts/postFeedModels.js';
import { availabilityHint, waitingMembersOf, lastCollectedFact } from '@/utils/productAvailability.js';

const props = defineProps({
  postId: {
    type: [String, Number],
    required: true,
  },
  currentFeed: {
    type: [String],
    required: true,
  },
  showBuyerActions: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'channel-click',
  'product-click',
  'share-success',
  'facebook-share',
]);

// Each feed reads its own table — `Bookmark` for the bookmarks tab,
// `TadbeesaPost` for the /tadbeesa/ page, `Post` for everything else — because
// each feed's reset flushes only its own (see models/posts/postFeedModels.js).
const currentRepo = computed(() => useRepo(postModelForFeed(props.currentFeed)));

const post = computed(() =>
  currentRepo.value.with('channel').with('products').with('media').find(props.postId),
);

const timeField = computed(() => {
  return props.currentFeed === 'bookmarks' ? post.value?.bookmarkedAt : post.value?.publishedAt;
});

// The two dated availability facts. Read off `post`, so the BOOKMARKS tab gets
// them for free: it is this same component with a repo swap above, and
// `Bookmark extends Post`, which inherits the field declarations that make the
// attributes exist at all. That matters more here than on the feed — a saved
// post stays in the bookmarks tab on its own window after the main feed has
// dropped it, so bookmarks is exactly where a member goes looking for something
// she wanted and where "can we still get this?" is the live question.
const availability = computed(() => availabilityHint(post.value));
const lastCollected = computed(() => lastCollectedFact(post.value));
// OTHER members waiting for this post's out-of-stock items. Omitted by the
// backend when zero, which the model hydrates to null — so only a positive
// whole number renders; anything else is "no chip", never "0 waiting".
const waitingMembers = computed(() => waitingMembersOf(post.value));
// Omitted-when-false on the wire; the model normalises that to false.
const isNew = computed(() => post.value?.isNew === true);

// The «تدبيسة» resale badge (BE→FE brief 2026-09-17-tadbeesa-channel-posts).
// NEVER omitted on the wire — read directly, unlike `isNew`/`availability`.
const isResale = computed(() => post.value?.isResale === true);
const resaleAvailableQty = computed(() => post.value?.resaleAvailableQty ?? 0);

const expanded = ref(false);
const currentContent = ref('');

// The second argument tells the page whether this is a «تدبيسة» post, whose
// header opens the /tadbeesa/ shelf. The PAGE decides and navigates with its
// own router — a card inside a virtual row never reaches for one.
const handleChannelClick = () => {
  if (post.value?.channelId) {
    emit('channel-click', post.value.channelId, { isResale: isResale.value });
  }
};

const handleProductClick = () => {
  if (post.value?.primaryProduct) {
    emit('product-click', post.value.primaryProduct);
  }
};

const handleShareSuccess = (data) => {
  emit('share-success', {
    postId: props.postId,
    ...data,
  });
};

const toggleExpand = () => {
  expanded.value = !expanded.value;
};

const handleContentUpdate = (content) => {
  currentContent.value = content;
};

const handleFacebookShare = () => {
  emit('facebook-share', props.postId);
};
</script>

<template>
  <f7-card v-if="post" class="post-card" outline>
    <post-header
      v-if="post.channel"
      :supplier="post.channel"
      :timestamp="timeField"
      :telegram-url="post.sourceUrl"
      @click="handleChannelClick"
    ></post-header>

    <post-media-section
      v-if="post.hasMedia"
      v-once
      :media="post.media"
      :product-name="post.primaryProduct?.name"
      :post-id="post.id"
    ></post-media-section>

    <post-content
      v-model:expanded="expanded"
      :content="post.content"
      :product-name="post.primaryProduct?.name"
      :post-id="post.id"
      :ai-content="post.aiContent"
      :show-buyer-actions="showBuyerActions"
      @toggle-expanded="toggleExpand"
      @update:current-content="handleContentUpdate"
    ></post-content>

    <!-- The two dated availability facts + the «جديد» tag.
         DELIBERATELY OUTSIDE any `v-once` subtree, and deliberately NOT inside
         <product-card>. `v-once` freezes a whole vnode subtree INCLUDING its
         prop bindings, and VirtualPostList keys rows by server post id — so a
         post that stays inside the overscan window is reused, not remounted. A
         badge rendered under `v-once` would go stale and stay stale while pinned
         on screen, which for an out-of-stock notice is the one failure that
         matters. It also keeps availability out of PostHeader's identity row
         (channel + the PoolShare gain chip): a caution strip beside a gain chip
         degrades both, and PostHeader renders for non-product posts where
         availability never applies.

         Each block is mounted on PRESENCE of its own fact — the classifier
         returns null when the key is absent, and absent is how the wire says
         "nothing to report". They are independent: a post can be new AND out of
         stock AND have been collected before. -->
    <div v-if="isNew || isResale || availability || lastCollected || waitingMembers" class="post-card__facts">
      <span v-if="isNew" class="post-card__new-tag">
        {{ $t('product.isNew.badge') }}
      </span>

      <!-- «تدبيسة» — a resale listing off the club's own shelf. The 6-form
           badge now carries the zero case too (تدبيسة P1-A part C) — its own
           zero form replaces the separate «نفدت» ternary this tag used to
           read; the FEED never shows a sold-out listing anyway (it drops out
           server-side), but this post's OWN page and a bookmarked read still
           answer at zero on purpose (2026-09-17 brief). At exactly one piece
           the tag switches to a filled style — the "hurry" moment. -->
      <span
        v-if="isResale"
        class="post-card__resale-tag"
        :class="{ 'post-card__resale-tag--last': resaleAvailableQty === 1 }"
      >
        {{ $t('product.resale.badge', { count: resaleAvailableQty }) }}
      </span>

      <!-- The certainty / in-box lines share one slot: whichever holds is the
           more useful fact right now. Once she holds pieces (post.cartItems),
           THAT is what she wants to read, regardless of how many others are
           still on the shelf — so it takes priority over the certainty line,
           which only ever mattered because she hadn't claimed anything yet. -->
      <div v-if="isResale && post.cartItems > 0" class="post-card__resale-in-box">
        <f7-icon f7="checkmark_circle_fill" size="14" class="post-card__resale-in-box-icon"></f7-icon>
        <span>{{ $t('product.resale.inBox', { count: post.cartItems }) }}</span>
      </div>
      <div v-else-if="isResale && resaleAvailableQty > 0" class="post-card__resale-certainty">
        <f7-icon f7="checkmark_circle_fill" size="14" class="post-card__resale-certainty-icon"></f7-icon>
        <span>{{ $t('product.resale.certainty') }}</span>
      </div>

      <availability-badge v-if="availability" :hint="availability"></availability-badge>

      <!-- How many other members want this too — the demand half of the badge
           beside it, and the reason the «مش متوفر» tab leads with the most
           wanted. A count only: who is waiting is never sent (owner G-J). -->
      <span v-if="waitingMembers" class="post-card__waiting-chip">
        {{ $t('product.waiting.chip', { count: waitingMembers }) }}
      </span>
      <last-collected-line v-if="lastCollected" :fact="lastCollected"></last-collected-line>

      <!-- «تعرف مين عنده؟» — the door for the member the badge is actually FOR.
           The suggestion door has always lived on an ORDER LINE, so the only
           person who could open it was someone who had already ordered the
           thing; the member reading «مش متوفر» here, before she orders, was the
           one person it refused.

           BESIDE THE BADGE, and only there: the API is deliberately NOT gated on
           the product being out of stock (a mark cleared while her sheet is open
           must not turn her tap into a 403), so WHERE to offer it is the app's
           decision, and the answer is "where she just read that we could not get
           it". It also carries her receipt once she has used it.

           It reads the tip off the POST, which unions its products' tips, and
           posts about the PRIMARY product — a post carries more than one product
           about 1% of the time and a card has room for one receipt.

           OUTSIDE the `v-once` subtrees for the same reason the badge is: rows
           are reused inside the overscan window, and a receipt frozen at mount
           would go stale while pinned on screen. -->
      <product-suggestion-door
        v-if="post.isProduct && availability && showBuyerActions"
        :product="post.primaryProduct"
        :source="post"
        :post-id="post.id"
      ></product-suggestion-door>
    </div>

    <product-card
      v-if="post.isProduct"
      v-once
      :product="post.primaryProduct"
      :post-media="post.media"
      :product-id="post.primaryProduct.id"
      :post-cart-items="post.cartItems"
      :show-buyer-actions="showBuyerActions"
      :is-resale="isResale"
      :resale-available-qty="resaleAvailableQty"
      @click="handleProductClick"
    ></product-card>
    <p v-else class="post-card__no-price">{{ $t('post.noPriceInPost') }}</p>

    <post-actions
      :post-id="post.id"
      :current-feed="currentFeed"
      :current-content="currentContent"
      :show-buyer-actions="showBuyerActions"
      @share-success="handleShareSuccess"
      @facebook-share="handleFacebookShare"
    ></post-actions>
  </f7-card>
</template>

<style scoped>
.post-card__no-price {
  margin: 6px 12px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--poolit-text-secondary);
}

.post-card {
  padding: 0 0 0 0px;
  margin: 0px 1px 0px 1px;
  border-radius: var(--f7-card-border-radius);
  transition: transform var(--poolit-transition-fast) var(--poolit-transition-ease),
  box-shadow var(--poolit-transition-fast) var(--poolit-transition-ease);
}

.post-card:active {
  transform: translateY(-2px);
}

.post-card__facts {
  display: flex;
  flex-direction: column;
  /* stretch (the default) so the availability strip runs the card's full width;
     the tag opts out below. */
}

/* «جديد» — a positive tag, so it takes the brand pair rather than the muted
   one the availability strip uses. Sized to sit quietly above the card's
   buying controls, not to compete with the product name. */
.post-card__new-tag {
  align-self: flex-start;
  margin: 8px 12px 0;
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--ci-brand-bg, #f0eaff);
  color: var(--ci-brand-fg, #6621e2);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
}

.post-card__waiting-chip {
  align-self: flex-start;
  margin: 8px 12px 0;
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--ci-warning-bg, #fff4e0);
  color: var(--ci-warning-fg, #8a5300);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
}

/* «تدبيسة» — a resale listing off the club's own shelf. A distinct hue from
   «جديد» above on purpose: this is a different kind of fact (where the piece
   came from, and how many are left), not a freshness signal. */
.post-card__resale-tag {
  align-self: flex-start;
  margin: 8px 12px 0;
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--poolit-warning-100, #fef3c7);
  color: var(--poolit-warning-700, #b45309);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
}

.dark .post-card__resale-tag {
  background: color-mix(in srgb, var(--poolit-warning-500) 20%, transparent);
  color: var(--poolit-warning-100, #fef3c7);
}

/* At exactly one piece the tag flips to a filled style — the "hurry" moment.
   Measured contrast (2026-09-23 design pass): #fff on warning-700 is 5.02:1,
   in both themes, so it needs no separate dark-mode value of its own — only
   an override AFTER `.dark .post-card__resale-tag` above so it isn't diluted
   back to the muted dark pair on a tied selector. */
.post-card__resale-tag--last {
  background: var(--poolit-warning-700, #b45309);
  color: #fff;
}

.dark .post-card__resale-tag--last {
  background: var(--poolit-warning-700, #b45309);
  color: #fff;
}

/* The certainty / in-box lines share one slot below the tag — see the
   template comment for why they're mutually exclusive. Measured contrast:
   #4b5563 on white is 7.56:1; success-700 on white is 5.48:1+. */
.post-card__resale-certainty,
.post-card__resale-in-box {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 6px 12px 0;
  font-size: 13px;
  line-height: 1.4;
}

.post-card__resale-certainty {
  color: #4b5563;
}

.post-card__resale-certainty-icon {
  color: var(--poolit-success-700);
  flex-shrink: 0;
}

.post-card__resale-in-box {
  color: var(--poolit-success-700);
  font-weight: 600;
}

.post-card__resale-in-box-icon {
  color: var(--poolit-success-700);
  flex-shrink: 0;
}
</style>
